import styles from './NavDrawer.scss';

import cx from 'classnames';
import React, { FC, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import { CSSTransitionClassNames } from 'react-transition-group/CSSTransition';
import { NavigationItem } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Icon, IconType } from 'src/components/Icon/Icon';
import { IconButton } from 'src/components/IconButton/IconButton';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { isBrowser } from 'src/utils/UserAgent';
import { INITIAL_UI_STATE } from '../UseUiController';
import { NAV_DRAWER } from './const';

export enum NavDrawerEffect {
  SLIDE = 'SLIDE',
  FADE = 'FADE',
}

export const navDrawerEffect: Record<NavDrawerEffect, CSSTransitionClassNames & { init: string }> = {
  [NavDrawerEffect.SLIDE]: {
    init: styles.navDrawerSlideInit,
    enter: styles.navDrawerSlideEnter,
    enterActive: styles.navDrawerSlideEnterActive,
    enterDone: styles.navDrawerSlideEnterDone,
    exit: styles.navDrawerSlideExit,
  },
  [NavDrawerEffect.FADE]: {
    init: styles.navDrawerFadeInit,
    enter: styles.navDrawerFadeEnter,
    enterActive: styles.navDrawerFadeEnterActive,
    enterDone: styles.navDrawerFadeEnterDone,
    exit: styles.navDrawerFadeExit,
  },
};
export interface NavDrawerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  titleEntry?: FlatEntry<NavigationItem>;
  defaultTitle?: string;
  isOpen?: boolean;
  noBgColor?: boolean;
  activeIndex?: number;
  effect?: NavDrawerEffect;
  onClickCloseButton?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const getEffectDuration = (effect: NavDrawerEffect): number => {
  switch (effect) {
    case NavDrawerEffect.FADE:
      return parseInt(styles.durationFade);
    case NavDrawerEffect.SLIDE:
    default:
      return parseInt(styles.durationSlide);
  }
};

export const NavDrawer: FC<NavDrawerProps> = ({
  onClickCloseButton,
  activeIndex,
  defaultTitle,
  isOpen,
  noBgColor,
  titleEntry,
  effect = NavDrawerEffect.SLIDE,
  children,
  className,
  ...props
}) => {
  const title = titleEntry?.title || defaultTitle;
  const { init, ...transitionClassNames } = navDrawerEffect[effect];
  const initEffectClassName = isOpen ? '' : init;
  const hasActiveItem = activeIndex !== undefined && activeIndex !== INITIAL_UI_STATE.activeMenuIndex;
  const noActiveItem = activeIndex !== undefined && activeIndex === INITIAL_UI_STATE.activeMenuIndex;
  const timeoutIdRef = useRef<NodeJS.Timeout>();

  // Toggle to disable the background page scroll when the drawer is open
  useEffect(() => {
    if (!isBrowser) return;
    if (isOpen) {
      timeoutIdRef.current = setTimeout(() => {
        document.documentElement.classList.add(styles.navDrawerScrollOverride);
      });
    } else {
      if (hasActiveItem) return;
      document.documentElement.classList.remove(styles.navDrawerScrollOverride);
    }
  }, [isOpen, hasActiveItem]);

  useEffect(() => {
    if (!isBrowser) return;
    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      document.documentElement.classList.remove(styles.navDrawerScrollOverride);
    };
  }, []);

  return (
    <CSSTransition
      in={isOpen}
      timeout={getEffectDuration(effect)}
      classNames={transitionClassNames}
      data-test={NAV_DRAWER.root}
      unmountOnExit
    >
      <div
        className={cx(
          styles.navDrawer,
          initEffectClassName,
          noBgColor && styles.navDrawerNoBgColor,
          className
        )}
        {...props}
        data-test={NAV_DRAWER.body}
      >
        <div
          className={cx(
            styles.navDrawerContent,
            noActiveItem && styles.navDrawerContentNoActiveItem
          )}
        >
          {onClickCloseButton && !title && (
            <IconButton
              type={IconType.CLOSE}
              className={styles.navDrawerCloseButton}
              onClick={onClickCloseButton}
            />
          )}
          {title && (
            <div className={styles.navDrawerHeader}>
              {onClickCloseButton ? (
                <button className={styles.navDrawerBackButton} onClick={onClickCloseButton}>
                  <Icon type={IconType.CHEVRON_LEFT} className={styles.navDrawerBackButtonIcon} />
                  <TextV2 className={styles.navDrawerTitle} value={title} variant="eyebrow" tagName="div" />
                </button>
              ) : (
                <TextV2 className={styles.navDrawerTitle} value={title} variant="eyebrow" tagName="div" />
              )}
            </div>
          )}
          <div>
            {children}
            <div className={styles.navDrawerSpacer} />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

NavDrawer.displayName = 'NavDrawer';
